/**
 * Aesthetic summary callout rendered at the top of a blog article body.
 *
 * What: Displays the article's TL;DR / Summary / Key takeaways section in a tinted, bordered
 *       card with a small uppercase label, matching modern editorial blog conventions
 *       (Stripe, Notion, Intercom, Figma). Inline markdown (bold, italic, links, inline code)
 *       is preserved inside bullets and paragraphs via ReactMarkdown.
 * Why:  Pulls the summary out of the body renderer so it can be styled distinctly and so the
 *       scannable "above-the-fold" content reads as a true summary rather than a normal H2
 *       section — improves GEO/SEO citability and first-paint comprehension.
 * When: Update when the article-summary visual language changes, when new summary kinds are
 *       added (e.g. grouped takeaways), or when DESIGN.md tokens for surfaces evolve.
 */
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import { Box, Stack, Typography } from '@mui/material';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { colors, radii } from '@/components/landing/landingTokens';
import type { BlogTldrContent } from '@/utils/blogTldrParser';

interface BlogTldrCalloutProps {
  tldr: BlogTldrContent;
  /** Override the label shown above the summary. Defaults to the parsed heading text. */
  labelOverride?: string;
}

// Inline-only component overrides so block elements (headings, lists) never render inside a
// single bullet or paragraph and break the card layout. `p` is unwrapped to a span so that
// list items don't get extra vertical spacing around a redundant paragraph.
const INLINE_MARKDOWN_COMPONENTS: Components = {
  p: ({ children }) => <>{children}</>,
  a: ({ href, children }) => (
    <Box
      component="a"
      href={href}
      sx={{
        color: colors.accentCobalt,
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        fontWeight: 600,
        '&:hover': { color: colors.accentOrange },
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 700, color: colors.ink }}>
      {children}
    </Box>
  ),
  em: ({ children }) => <Box component="em">{children}</Box>,
  code: ({ children }) => (
    <Box
      component="code"
      sx={{
        fontFamily:
          "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
        fontSize: '0.9em',
        px: 0.6,
        py: 0.1,
        borderRadius: 4,
        bgcolor: colors.backgroundAlt,
        color: colors.ink,
      }}
    >
      {children}
    </Box>
  ),
  // Block elements inside a single bullet/paragraph are unexpected — flatten to children so
  // malformed content never breaks the layout.
  h1: ({ children }) => <>{children}</>,
  h2: ({ children }) => <>{children}</>,
  h3: ({ children }) => <>{children}</>,
  h4: ({ children }) => <>{children}</>,
  ul: ({ children }) => <>{children}</>,
  ol: ({ children }) => <>{children}</>,
  li: ({ children }) => <>{children}</>,
  blockquote: ({ children }) => <>{children}</>,
};

function InlineMarkdown({ source }: { source: string }) {
  return (
    <ReactMarkdown
      skipHtml
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSanitize]}
      components={INLINE_MARKDOWN_COMPONENTS}
    >
      {source}
    </ReactMarkdown>
  );
}

function BlogTldrCallout({ tldr, labelOverride }: BlogTldrCalloutProps) {
  const displayLabel =
    (labelOverride ?? tldr.label ?? 'TL;DR').trim() || 'TL;DR';

  return (
    <Box
      component="aside"
      aria-label={`Article summary: ${displayLabel}`}
      sx={{
        position: 'relative',
        // Asymmetric margins: hug the header above (the callout reads as a summary tied to
        // the byline), breathe into the opening paragraph below.
        mt: { xs: 1.5, sm: 2, md: 2.5 },
        mb: { xs: 3, sm: 4, md: 5 },
        px: { xs: 2.75, sm: 3.5, md: 4 },
        py: { xs: 2.75, sm: 3.25, md: 3.5 },
        borderRadius: radii.md,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${colors.accentCobalt}`,
        bgcolor: colors.background,
        boxShadow: '0 1px 2px rgba(26, 30, 48, 0.04)',
      }}
    >
      <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: colors.accentCobalt,
          }}
        >
          {displayLabel}
        </Typography>

        {tldr.kind === 'list' ? (
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              m: 0,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1, sm: 1.25 },
            }}
          >
            {tldr.items.map((raw, index) => (
              <Box
                key={index}
                component="li"
                sx={{
                  position: 'relative',
                  pl: { xs: 2.25, sm: 2.5 },
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                  lineHeight: 1.6,
                  color: colors.ink,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: { xs: '0.55em', sm: '0.6em' },
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: colors.accentCobalt,
                  },
                }}
              >
                <InlineMarkdown source={raw} />
              </Box>
            ))}
          </Box>
        ) : (
          <Stack spacing={{ xs: 1, sm: 1.25 }}>
            {tldr.items.map((raw, index) => (
              <Typography
                key={index}
                component="p"
                sx={{
                  m: 0,
                  fontSize: { xs: '0.9375rem', sm: '1rem' },
                  lineHeight: 1.65,
                  color: colors.ink,
                }}
              >
                <InlineMarkdown source={raw} />
              </Typography>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default BlogTldrCallout;
